import React from 'react';
import { Box, Chip, Tooltip, Typography } from '@mui/material';
import { CheckCircle, Error, Warning, CreditCard } from '@mui/icons-material';

interface PaymentVerificationStatusProps {
  verificationStatus?: 'verified' | 'pending' | 'failed';
  verificationDate?: string;
  verificationAmount?: string;
  cardLast4?: string;
  cardBrand?: string;
}

const PaymentVerificationStatus: React.FC<PaymentVerificationStatusProps> = ({
  verificationStatus = 'pending',
  verificationDate,
  verificationAmount,
  cardLast4,
  cardBrand
}) => {
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'verified':
        return 'success';
      case 'failed':
        return 'error';
      case 'pending':
      default:
        return 'warning';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'verified':
        return <CheckCircle />;
      case 'failed':
        return <Error />;
      case 'pending':
      default:
        return <Warning />;
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'verified':
        return 'Verified';
      case 'failed':
        return 'Verification Failed';
      case 'pending':
      default:
        return 'Pending Verification';
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'Unknown';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getTooltipText = () => {
    switch (verificationStatus) {
      case 'verified':
        return `Card verified on ${formatDate(verificationDate)} with $${verificationAmount} authorization. Card ending in ${cardLast4} (${cardBrand}) is ready for payments.`;
      case 'failed':
        return `Card verification failed. Customer needs to update their payment method.`;
      case 'pending':
      default:
        return `Card verification is pending. Customer needs to complete payment setup.`;
    }
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <CreditCard sx={{ fontSize: 16, color: 'text.secondary' }} />
      <Tooltip title={getTooltipText()} arrow>
        <Chip
          icon={getStatusIcon(verificationStatus)}
          label={getStatusText(verificationStatus)}
          color={getStatusColor(verificationStatus) as any}
          size="small"
          variant="outlined"
        />
      </Tooltip>
      {verificationStatus === 'verified' && cardLast4 && (
        <Typography variant="caption" color="text.secondary">
          •••• {cardLast4}
        </Typography>
      )}
    </Box>
  );
};

export default PaymentVerificationStatus;
